import { PresentationSlides } from '@core/presentation-panel';
import { useChunkUpload } from '@core/shared';
import { BaseApiResponse, OptionsApi, SlideDeckStatus } from '@core/swagger-client';
import { WebinarStatus } from '@core/types';
import {
    Check,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    ErrorMessage,
    Heading,
    PageHeader,
    PageHeaderContent,
    WarningOutlined,
    useFileDropzone
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { Trans, useTranslation } from 'react-i18next';
import { DeleteSlideButton } from '../../atoms/deleteSlideButton';
import { SlideFileUpload } from '../../molecules';

export const Slide = () => {
    const { webinarId } = useParams({ strict: false });
    const { data: webinar, isLoading: webinarIsLoading } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));

    const [refetchIntervalMs, setRefetchIntervalMs] = useState(0);
    const [readOnly, setReadOnly] = useState<boolean>(false);

    useEffect(() => {
        if (!webinar) {
            return;
        }

        setReadOnly(webinar.status !== WebinarStatus.Planned && webinar.status !== WebinarStatus.Started);
    }, [webinar]);

    const { upload, isUploading, progress } = useChunkUpload(`/api/slide-deck/${webinarId}/upload`);
    const {
        data: slideInfo,
        isLoading: slideInfoIsLoading,
        refetch: refetchSlideInfo
    } = useQuery(
        OptionsApi.slideDeck.get(webinarId!, {
            refetchInterval: refetchIntervalMs
        })
    );

    useEffect(() => {
        if (!slideInfo) {
            return;
        }

        if (slideInfo.status === SlideDeckStatus.Processing || slideInfo.status === SlideDeckStatus.Queued) {
            setRefetchIntervalMs(1000);
            setIsProcessing(true);
        } else {
            setRefetchIntervalMs(0);
            setIsProcessing(false);
        }
    }, [slideInfo]);

    const [uploadResult, setUploadResult] = useState<BaseApiResponse | null>(null);
    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: async (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                const response = await upload(acceptedFiles[0]);
                setUploadResult(response);

                if (response.success) {
                    refetchSlideInfo();

                    // The work-around is to also set the refetchInterval to 50ms
                    setRefetchIntervalMs(50);
                }
            }
        },
        accept: {
            'application/pdf': ['.pdf']
        }
    });

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onDeleteSlideClick = () => {
        deleteWebinarSlideMutation.mutate(webinarId!, {
            onSuccess: () => {
                setUploadResult(null);

                refetchSlideInfo();
                setRefetchIntervalMs(50);
            }
        });
    };

    const deleteWebinarSlideMutation = useMutation(OptionsApi.slideDeck.delete());

    const { t } = useTranslation();

    useEffect(() => {
        setIsLoading(slideInfoIsLoading || webinarIsLoading || deleteWebinarSlideMutation.isPending);
    }, [slideInfoIsLoading, webinarIsLoading, deleteWebinarSlideMutation.isPending]);

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="content" />
                <ContentHeader contentHeader={t('Content.Slides.PageHeading')} testId="slideManagement" />
                <div className="w-full max-w-4xl">
                    {isLoading && (
                        <ContentLoader speed={2} width={1250} height={300} backgroundColor="#C0C0C0" foregroundColor="#ecebeb">
                            <rect x="60" y="5" width="560" height="150" />
                        </ContentLoader>
                    )}

                    {!isLoading && (
                        <>
                            {readOnly && <div>{t('Content.Slides.ReadOnly')}</div>}
                            <div className="relative min-h-64 flex mb-8 lg:mb-16">
                                {isUploading && (
                                    <div className="relative flex-1 flex flex-col items-center justify-center bg-white border-dashed border-2 border-gray-400 rounded-lg p-6">
                                        <label htmlFor="progress-bar" className="mb-2">
                                            {t('Content.Attachments.Progress.Label')}
                                        </label>
                                        <progress id="progress-bar" className="progress" value={progress} max="100">
                                            {progress}%
                                        </progress>
                                    </div>
                                )}
                                {/* no valid webinar slides and user hasn't selected a file yet */}
                                {!readOnly && !slideInfo?.filename && !isUploading && (
                                    <div
                                        {...getRootProps()}
                                        className={`group w-full ${isDragActive ? 'bg-accent-lighter' : 'bg-white'} hover:bg-accent-lighter focus-visible:bg-accent-lighter border-dashed border-2 ${isDragActive ? 'border-accent' : 'border-gray-400'} ${(rejectionCode || (uploadResult && !uploadResult.success)) && !isDragActive && 'border-warning'} hover:border-accent focus-visible:border-accent rounded-lg flex flex-col items-center justify-center p-6 outline-hidden transition-color duration-150 ease-in-out`}
                                    >
                                        <input data-testid="drop-input" {...getInputProps()} name="slidesFile" />
                                        <div data-testid="dragAndDrop" className="flex flex-col items-center ">
                                            <SlideFileUpload isDragActive={isDragActive} />
                                        </div>
                                        {rejectionCode ? (
                                            <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />
                                        ) : uploadResult && !uploadResult.success ? (
                                            <ErrorMessage apiError={uploadResult} classNames="mt-4" />
                                        ) : (
                                            <p className="mt-8 mb-4">
                                                {t('Generic.DragDrop.AcceptedText')}
                                                <Trans components={{ bold: <strong className="font-medium" /> }} i18nKey="Content.Slides.Restrictions" />
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* webinar has valid slides */}
                                {!!slideInfo?.filename && (
                                    <div className="relative flex-1 flex flex-col items-between justify-center bg-white border-dashed border-2 border-gray-400 rounded-lg p-6">
                                        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                                            <div className="max-w-full min-w-0">
                                                {t('Content.Slides.FileLabel')}
                                                <p className="wrap-break-word font-medium text-lg">{slideInfo!.filename}</p>
                                            </div>
                                            <div className="w-full sm:w-auto flex items-center justify-between ">
                                                <div className="mr-4 whitespace-nowrap">
                                                    {t('Content.Slides.SlidesAmountLabel')}
                                                    <p className="font-medium text-lg">
                                                        {slideInfo!.slides.length}
                                                        {isProcessing ? ` / ${slideInfo!.totalSlides}` : ''}
                                                    </p>
                                                </div>
                                                {!readOnly && !isProcessing && webinarId && <DeleteSlideButton onDeleteSlideClick={onDeleteSlideClick} />}
                                            </div>
                                        </div>
                                        {isProcessing && slideInfo.status === SlideDeckStatus.Queued && (
                                            <div className="flex justify-center">
                                                <p className="flex items-center bg-success bg-opacity-30 p-2 rounded-md mt-5 text-sm">
                                                    <Check classNames="size-6 inline mr-2 stroke-current flex-none" />
                                                    {t('Content.Slides.Status.Queued')}
                                                </p>
                                            </div>
                                        )}
                                        {isProcessing && slideInfo.status === SlideDeckStatus.Processing && (
                                            <div className="flex justify-center">
                                                <p className="flex items-center bg-success bg-opacity-30 p-2 rounded-md mt-5 text-sm">
                                                    <Check classNames="size-6 inline mr-2 stroke-current flex-none" />
                                                    {t('Content.Slides.Status.Processing')}
                                                </p>
                                            </div>
                                        )}
                                        {slideInfo.status === SlideDeckStatus.Error && (
                                            <div className="flex justify-center">
                                                <p className="flex items-center bg-success bg-opacity-30 p-2 rounded-md mt-5 text-sm">
                                                    <WarningOutlined classNames="size-6 inline mr-2 fill-current flex-none" />
                                                    {t('Content.Slides.Status.Error')}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {slideInfo?.filename && (
                                <div className="mb-10">
                                    <Heading variant="h2" classNames={`mb-4 lg:mb-6 2xl:mb-8`}>
                                        {t('Content.Slides.PreviewHeading')}
                                    </Heading>
                                    <div className="p-6 bg-white rounded-lg relative">
                                        <PresentationSlides
                                            slides={slideInfo?.slides ?? []}
                                            currentSlideNumber={0}
                                            totalSlides={slideInfo ? slideInfo.slides?.length : 0}
                                            classNames="block h-full w-full object-contain"
                                            showFullScreen="hidden"
                                            isPreview
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
